import AddonButton from '../../elements/buttons/addonButton';
import AddonContext from '../../../../shared/components/utils/context/addonContext';
import DetailAddonChannelsContainer from '../../atoms/container/detailAddonChannelsContainer';
import DetailAddonChannelLogo from '../../atoms/images/detailAddonChannelLogo';
import Focus from '../../../../shared/types/enums/focus';
import Modals from '../../../../shared/types/enums/modals';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import User from '../../../../shared/classes/user';
import addonFreeRequest from '../../../../shared/api/addons/addonFreeRequest';
import addPlanRequest from '../../../../shared/api/addPlan/request';
import useImagePreload from '../../../../shared/components/utils/hooks/useImagePreload';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import {
  addExtraPlan,
  userSelector,
} from '../../../../shared/store/slices/user.slice';
import {
  focusSelector,
  setFocus,
} from '../../../../shared/store/slices/focus.slice';
import { loadChannels } from '../../../../shared/store/thunks/channels.thunk';
import { loginAction } from '../../../../shared/actions/flow';
import {
  setModal,
  setModalParams,
} from '../../../../shared/store/slices/modal.slice';
import { useAppDispatch } from '../../../../shared/store/hooks';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const BUTTON_CHANNELS_OFFSET = 3;
const INLINE_CHANNELS = 12;

function DetailAddonChannels() {
  const dispatch = useAppDispatch();
  const { addon, channels } = useContext(AddonContext);
  const { focus } = useSelector(focusSelector);
  const { isAnonymous } = useSelector(userSelector);
  const [canClick, setCanClick] = useState(true);

  const buttonText = addon?.isFree
    ? isAnonymous
      ? TranslationManager.get('PAGES.ADDON.LOGIN')
      : TranslationManager.get('PAGES.ADDON.ADD_FREE')
    : TranslationManager.get('PAGES.ADDON.WANT_IT');

  const addonRef = useRef(addon);
  const canClickRef = useRef(true);
  const isFreeRef = useRef(addon?.isFree);
  const focusRef = useRef(focus);
  const isAnonymousRef = useRef(isAnonymous);
  addonRef.current = addon;
  canClickRef.current = canClick;
  focusRef.current = focus;
  isAnonymousRef.current = isAnonymous;
  isFreeRef.current = addon?.isFree;

  const addonAction = useCallback(() => {
    if (isAnonymousRef.current) {
      isFreeRef.current
        ? loginAction()
        : dispatch(setModal(Modals.ANONYMOUS_GENERIC));
      return;
    }
    setCanClick(false);
    (isFreeRef.current
      ? addonFreeRequest(addonRef.current?.planId)
      : addPlanRequest({
          addonPlanCode: addonRef.current?.plan?.code,
        })
    )
      .then(() => {
        dispatch(setModalParams({ addon: addonRef.current }));
        if (isFreeRef.current) {
          const plan = User.getPlan(addonRef.current?.planId);
          dispatch(addExtraPlan(plan));
          dispatch(loadChannels());

          dispatch(setModal(Modals.SUBSCRIBE_ADDON));
        } else {
          dispatch(setModal(Modals.UPGRADE_ADDON));
        }
      })
      .catch(() => {
        dispatch(
          setModalParams({
            text: TranslationManager.get('MODAL.GENERIC_ERROR.REQUEST'),
          })
        );
        dispatch(setModal(Modals.GENERIC_ERROR));
      })
      .finally(() => {
        setCanClick(true);
      });
  }, []);

  useEffect(() => {
    dispatch(setFocus(Focus.DETAIL_BUTTONS));
  }, []);

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      switch (key) {
        case 'UP':
          dispatch(setFocus(Focus.DETAIL_BUTTONS));
          break;
        case 'DOWN':
          // dispatch(setFocus(Focus.RAILS)) ; TODO: Add related rail
          break;
        case 'SELECT':
          focusRef.current === Focus.DETAIL_BUTTONS &&
            canClickRef.current &&
            addonAction();
          break;
      }
    },
  });

  const logos = useImagePreload(channels?.map((channel) => channel.logo));

  const displayButton = addonRef.current?.plan?.code;
  const displayChannels = !!logos?.length;

  return displayButton || displayChannels ? (
    <DetailAddonChannelsContainer
      split={
        (logos?.length || 0) >
        INLINE_CHANNELS + (displayButton ? 0 : BUTTON_CHANNELS_OFFSET)
      }
    >
      <>
        {displayButton ? (
          <AddonButton
            canClick={canClick}
            focus={Focus.DETAIL_BUTTONS}
            item={buttonText}
            releaseAction={addonAction}
          />
        ) : null}
        {displayChannels
          ? logos.map((item, index) => {
              return <DetailAddonChannelLogo key={index} src={item.src} />;
            })
          : null}
      </>
    </DetailAddonChannelsContainer>
  ) : null;
}

export default DetailAddonChannels;
